// Real-time WebSocket client for GuestRise Tickets.
//
// Connects to wss://ws.rsv.mx and emits `identify` with the active user JWT.
// On a successful identify the server joins us to room client:{N} so we
// receive client-scoped events; failed identifies still receive global
// broadcasts.

import { io, type Socket } from 'socket.io-client';

const REALTIME_URL = 'wss://ws.rsv.mx';

const BACKEND_EVENTS = [
  'event.store.paused',
  'event.store.resumed',
  'event.feedback.negative',
  'event.companion.disconnected',
  'event.companion.reconnected',
  'event.order.large',
  'event.order.refunded',
  'event.inventory.critical',
  'event.cron.failure',
] as const;

export type RealtimeEvent = typeof BACKEND_EVENTS[number];

export interface RealtimeOptions {
  appType?: string;
  getAuthToken: () => string | null | Promise<string | null>;
  station?: string | null;
  onConnect?: (sid: string) => void;
  onDisconnect?: (reason: string) => void;
  onEvent?: (event: RealtimeEvent, data: unknown) => void;
}

let socket: Socket | null = null;

export function connectRealtime(options: RealtimeOptions): Socket {
  const { appType = 'tickets', getAuthToken, station = null, onConnect, onDisconnect, onEvent } = options;

  if (socket?.connected) return socket;
  if (socket) {
    try { socket.removeAllListeners(); socket.disconnect(); } catch { /* noop */ }
    socket = null;
  }

  socket = io(REALTIME_URL, {
    transports: ['websocket'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    reconnectionAttempts: Infinity,
  });

  socket.on('connect', async () => {
    const s = socket!;
    console.log('[realtime] connected', s.id);
    let token: string | null = null;
    try {
      const maybe = await Promise.resolve(getAuthToken());
      token = maybe || null;
    } catch (err) {
      console.warn('[realtime] getAuthToken threw:', err);
    }
    const payload: Record<string, unknown> = { type: appType, auth_token: token };
    if (station) payload.station = station;
    s.emit('identify', payload);
    if (onConnect) {
      try { onConnect(s.id || ''); } catch (err) { console.warn('[realtime] onConnect threw:', err); }
    }
  });

  socket.on('disconnect', (reason: string) => {
    console.warn('[realtime] disconnected:', reason);
    if (onDisconnect) {
      try { onDisconnect(reason); } catch (err) { console.warn('[realtime] onDisconnect threw:', err); }
    }
  });

  socket.on('connect_error', (err: Error) => {
    console.error('[realtime] connect error:', err?.message || err);
  });

  BACKEND_EVENTS.forEach((eventName) => {
    socket!.on(eventName, (data: unknown) => {
      if (onEvent) {
        try { onEvent(eventName, data); } catch (err) { console.warn('[realtime] onEvent threw for', eventName, err); }
      }
    });
  });

  return socket;
}

export function disconnectRealtime(): void {
  if (!socket) return;
  try { socket.removeAllListeners(); socket.disconnect(); } catch { /* noop */ }
  socket = null;
}

export function getRealtimeSocket(): Socket | null {
  return socket;
}

export function realtimeEventLabel(eventName: string): string {
  const map: Record<string, string> = {
    'event.store.paused': 'Tienda pausada',
    'event.store.resumed': 'Tienda reactivada',
    'event.companion.disconnected': 'Companion desconectado',
    'event.companion.reconnected': 'Companion reconectado',
    'event.order.large': 'Pedido grande',
    'event.feedback.negative': 'Mala calificación',
    'event.order.refunded': 'Refund',
    'event.inventory.critical': 'Inventario crítico',
    'event.cron.failure': 'Cron falló',
  };
  return map[eventName] || eventName;
}
